import { EventType } from "@/analytics/analytics-events";
import {
  convertDistanceToMeter,
  formatDistanceDecimal,
  getDistanceUnitSymbolText,
} from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectAreaDynamicMaps } from "@/features/dynamic-overview-map/dynamic-map-selectors";
import { useCurrentScene } from "@/modes/mode-data-context";
import { useCanOrderFromIpx } from "@/modes/ipx-order-mode/use-can-order-from-ipx";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { changeMode } from "@/store/mode-slice";
import { setImportSheetAreaId } from "@/store/modes/import-sheet-mode-slice";
import { startIpxOrderWizard } from "@/store/modes/ipx-order-mode-thunks";
import {
  selectActiveArea,
  selectListVisibleSheets,
} from "@/store/selections-selectors";
import {
  setSheetTheOnlyVisible,
  setSheetVisibility,
} from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { setShowSpinner } from "@/store/ui/ui-slice";
import { selectHasWritePermission } from "@/store/user-selectors";
import {
  BuildingElevationIcon,
  FaroIconButton,
  FaroMenu,
  FaroMenuProps,
  FaroPopover,
  FaroText,
  FaroTooltip,
  grey,
  HistoryUndoIcon,
  InfoIcon,
  LocationMapIcon,
  NoTranslate,
  PlusIcon,
  SingleSelectItem,
  TextField,
  TranslateVar,
  TruncatedFaroText,
  useDialog,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID, type DistanceUnitTypes } from "@faro-lotv/foundation";
import {
  IElementDynamicTiledMap,
  IElementGenericImgSheet,
  IElementSection,
} from "@faro-lotv/ielement-types";
import { useApiClientContext } from "@faro-lotv/service-wires";
import {
  ButtonBase,
  Divider,
  InputAdornment,
  Stack,
  Tooltip,
} from "@mui/material";
import { useCallback, useMemo, useRef, useState, type JSX } from "react";
import {
  MultiSheetMenuItemGroup,
  MultiSheetMenuItemGroupProps,
  selectMultiSheetMenuItemGroups,
} from "./multi-sheet-menu-item-group";
import {
  applySheetsElevation,
  getSheetsElevation,
} from "./set-sheet-elevation-utils";

const NUMBER_PRECISION_REGEX = /\.\d{3,}$/;

export type MultiSheetMenuProps = Pick<
  MultiSheetMenuBaseProps,
  "open" | "anchorEl" | "onClose"
>;

/**
  @returns a MultiSheetMenu connected with the app store
 * @param props The properties of the MultiSheetMenu
 */
export function MultiSheetMenu(props: MultiSheetMenuProps): JSX.Element {
  const { availableSheets, activeSheets, referenceElement } = useCurrentScene();
  const activeArea = useAppSelector(selectActiveArea);
  const listVisibleSheets = useAppSelector(selectListVisibleSheets);
  const hasDynamicMaps = useAppSelector(
    selectHasFeature(Features.DynamicOverviewMap),
  );
  const dynamicMaps = useAppSelector(
    selectAreaDynamicMaps(activeArea, referenceElement),
  );

  const dispatch = useAppDispatch();

  const isSheetVisible = useCallback(
    (sheetId: GUID) => listVisibleSheets.includes(sheetId),
    [listVisibleSheets],
  );

  return (
    <MultiSheetMenuBase
      {...props}
      availableSheets={availableSheets}
      activeSheets={activeSheets}
      dynamicMaps={hasDynamicMaps ? dynamicMaps : undefined}
      activeArea={activeArea}
      listVisibleSheets={listVisibleSheets}
      isSheetVisible={isSheetVisible}
      onElementClicked={(id) => dispatch(setSheetTheOnlyVisible(id))}
      onEyeClicked={(sheetId, isVisible) =>
        dispatch(setSheetVisibility({ sheetId, isVisible }))
      }
    />
  );
}

// queryAreaVolume needs a signal, but this refetch must finish even if the menu closes (the mutation
// is already saved), so we share one signal that is never aborted rather than tying it to the button.
const ELEVATION_QUERY_SIGNAL = new AbortController().signal;

type SetSheetElevationButtonProps = {
  /** The current active area */
  activeArea?: IElementSection;
  /** A sheet of the active area, used to read the sheets' current elevation */
  sheet?: IElementGenericImgSheet;
};

function SetSheetElevationButton({
  activeArea,
  sheet,
}: SetSheetElevationButtonProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { projectApiClient } = useApiClientContext();
  const { handleErrorWithToast } = useErrorHandlers();
  const { createDialog, setConfirmButtonDisabled } = useDialog();

  // Read the unit here, because the dialog content is rendered in a different place than the
  // SettingsContextProvider , so it cannot read the unit itself.
  const distanceUnit = useDistanceUnit();

  const defaultElevation = useMemo(
    () => getSheetsElevation(sheet, activeArea),
    [sheet, activeArea],
  );

  // Synced value of the elevation, in meters, to be set when the user confirms the dialog.
  const elevationValueRef = useRef(defaultElevation);

  const onNumberFieldError = useCallback(
    (error?: string) => {
      setConfirmButtonDisabled(error !== undefined);
    },
    [setConfirmButtonDisabled],
  );

  const updateSheetElevation = useCallback(async () => {
    try {
      Analytics.track(EventType.setAreaElevation);

      // Need to check for exactly undefined otherwise 0 is treated as falsy and the function will return early
      if (!activeArea || elevationValueRef.current === undefined) return false;

      dispatch(setShowSpinner(true));

      await applySheetsElevation({
        projectApiClient,
        dispatch,
        areaId: activeArea.id,
        elevation: elevationValueRef.current,
        signal: ELEVATION_QUERY_SIGNAL,
      });

      return true;
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to set sheets elevation",
        error,
      });
      return false;
    } finally {
      // Javascript specs guarantee that the finally block is executed just before the function returns the result
      dispatch(setShowSpinner(false));
    }
  }, [activeArea, dispatch, handleErrorWithToast, projectApiClient]);

  const showAreaElevationDialog = useCallback(() => {
    if (!activeArea) return;

    // Keep the current elevation value in sync with the default value,
    // so that if the user opens the dialog again, it will show the current elevation.
    elevationValueRef.current = defaultElevation;

    createDialog({
      title: "Set sheets elevation",
      content: (
        <SheetsElevationDialogContent
          areaName={activeArea.name}
          defaultElevation={defaultElevation}
          distanceUnit={distanceUnit}
          onErrorChange={onNumberFieldError}
          onValueChange={(value) => {
            elevationValueRef.current = value;
          }}
        />
      ),
      onConfirm: updateSheetElevation,
    });
  }, [
    activeArea,
    createDialog,
    defaultElevation,
    distanceUnit,
    onNumberFieldError,
    updateSheetElevation,
  ]);

  return (
    <FaroTooltip title="Set sheets elevation">
      <FaroIconButton
        size="xs"
        aria-label="sheets-elevation"
        onClick={showAreaElevationDialog}
      >
        <BuildingElevationIcon sx={{ stroke: grey[100] }} />
      </FaroIconButton>
    </FaroTooltip>
  );
}

export type MultiSheetMenuBaseProps = Pick<
  FaroMenuProps,
  "open" | "anchorEl" | "onClose"
> & {
  /** The list of sheets available given the current user selection */
  availableSheets: IElementGenericImgSheet[];
  /** The list of visible sheets */
  activeSheets: IElementGenericImgSheet[];
  /** The dynamic overview maps available in the area, listed as disabled entries for now */
  dynamicMaps?: IElementDynamicTiledMap[];
  /** The current active area */
  activeArea: IElementSection | undefined;
} & Pick<
    MultiSheetMenuItemGroupProps,
    | "listVisibleSheets"
    | "onElementClicked"
    | "onEyeClicked"
    | "isSheetVisible"
    | "showEditOptions"
  >;

/** @returns a PopOver to open from a menu to change the sheet rendered in the scene*/
export function MultiSheetMenuBase({
  open,
  anchorEl,
  onClose,
  availableSheets,
  activeSheets,
  dynamicMaps,
  activeArea,
  listVisibleSheets,
  showEditOptions = true,
  isSheetVisible,
  onElementClicked,
  onEyeClicked,
}: MultiSheetMenuBaseProps): JSX.Element {
  const originalSheetGroups = useAppSelector(
    selectMultiSheetMenuItemGroups(availableSheets),
  );
  // Sort the sheets inside each group by name, using the user's locale for case-insensitive sorting
  const sortedSheetGroups = useMemo(() => {
    // allocate a new object to avoid mutating the original state
    const sorted = originalSheetGroups.map((sheetGroup) => {
      const newGroup = { ...sheetGroup, sheets: [...sheetGroup.sheets] };
      newGroup.sheets.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
      return newGroup;
    });
    return sorted;
  }, [originalSheetGroups]);

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null);

  const dispatch = useAppDispatch();
  const hasWritePermission = useAppSelector(selectHasWritePermission);
  const canOrderFromIpx = useCanOrderFromIpx();

  const shouldBeDarkStyle = true;

  const numberVisibleSheetsInThisArea = useMemo(
    () =>
      activeSheets.reduce(
        (numberVisible: number, sheet) =>
          listVisibleSheets.includes(sheet.id)
            ? numberVisible + 1
            : numberVisible,
        0,
      ),
    [activeSheets, listVisibleSheets],
  );

  return (
    <FaroMenu
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: "top",
        horizontal: -2,
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      onClose={onClose}
      dark={shouldBeDarkStyle}
    >
      <Stack sx={{ minWidth: "291px" }}>
        <Stack direction="row" sx={{ justifyContent: "space-between", p: 1.5 }}>
          <Stack
            direction="row"
            sx={{ alignContent: "center", alignItems: "center" }}
          >
            <FaroText
              variant="heading14"
              color={grey[100]}
              sx={{ mt: "1", mb: "1" }}
            >
              Sheets
            </FaroText>

            {activeArea && (
              <FaroIconButton
                size="xs"
                aria-label="sheet-info"
                ref={setButtonRef}
                onClick={() => {
                  setIsPopoverOpen(true);
                }}
                color={grey[100]}
                sx={{ ml: "6" }}
              >
                <InfoIcon sx={{ stroke: grey[100] }} />
              </FaroIconButton>
            )}
          </Stack>
          <Stack direction="row">
            {hasWritePermission && (
              // All sheets in an area share one elevation, so any sheet is a valid source for it.
              <SetSheetElevationButton
                activeArea={activeArea}
                sheet={availableSheets.at(0)}
              />
            )}

            {activeArea && hasWritePermission && showEditOptions && (
              <Tooltip title="Add sheet">
                <FaroIconButton
                  size="xs"
                  aria-label="add-sheet"
                  onClick={() => {
                    Analytics.track(EventType.startImportSheetWorkflow);
                    dispatch(setImportSheetAreaId(activeArea.id));
                    dispatch(changeMode("importSheet"));
                  }}
                >
                  <PlusIcon sx={{ stroke: grey[400] }} />
                </FaroIconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {sortedSheetGroups.map((group, index) => (
          <MultiSheetMenuItemGroup
            key={index}
            group={group}
            isFirstGroup={index === 0}
            sheetsInArea={availableSheets.length}
            visibleSheetsInArea={numberVisibleSheetsInThisArea}
            listVisibleSheets={listVisibleSheets}
            isSheetVisible={isSheetVisible}
            onElementClicked={onElementClicked}
            onEyeClicked={onEyeClicked}
            showEditOptions={showEditOptions}
          />
        ))}

        {!!dynamicMaps?.length && (
          <>
            <Divider sx={{ backgroundColor: grey[800], my: "0 !important" }} />
            {dynamicMaps.map((map) => (
              <DynamicMapMenuItem key={map.id} map={map} />
            ))}
          </>
        )}

        {canOrderFromIpx && showEditOptions && (
          <ButtonBase
            aria-label="generate from point cloud"
            onClick={() => dispatch(startIpxOrderWizard("sheetsMenu"))}
            sx={{
              justifyContent: "flex-start",
              gap: 1.5,
              mx: 1.5,
              my: 1,
              p: 1.5,
              minHeight: 48,
              borderRadius: 1,
              border: `1px dashed ${grey[600]}`,
              textAlign: "left",
            }}
          >
            <PlusIcon sx={{ stroke: grey[100] }} />
            <Stack>
              <FaroText variant="labelM" color={grey[100]}>
                Generate from point cloud
              </FaroText>
              <FaroText variant="bodyS" color={grey[400]}>
                Floor plans and BIM models by IPX
              </FaroText>
            </Stack>
          </ButtonBase>
        )}

        <FaroPopover
          anchorEl={buttonRef}
          open={isPopoverOpen}
          closeOnClickOutside
          dark={shouldBeDarkStyle}
          description="Shows the sheets in the active area. Overview maps based on the active point cloud are listed below."
          onClose={() => {
            setIsPopoverOpen(false);
          }}
          placement="bottom"
          title="Sheets"
        />
      </Stack>
    </FaroMenu>
  );
}

type DynamicMapMenuItemProps = {
  /** The dynamic overview map to list */
  map: IElementDynamicTiledMap;
};

/** @returns a disabled entry listing a dynamic overview map, until it can be rendered */
function DynamicMapMenuItem({ map }: DynamicMapMenuItemProps): JSX.Element {
  return (
    <SingleSelectItem
      value={map.id}
      disabled
      Icon={LocationMapIcon}
      label={
        <TruncatedFaroText variant="bodyM" color="inherit" dark>
          <NoTranslate component="span">{map.name}</NoTranslate>
        </TruncatedFaroText>
      }
      /** TODO: The secondary text should be removed in https://faro01.atlassian.net/browse/SWEB-8909 */
      secondaryText="Coming soon"
      dark
    />
  );
}

/**
 * Format an elevation (in meters) into display text in the given unit, rounded
 * to two decimal digits. Returns an empty string when the elevation is undefined.
 *
 * @param elevationInMeters The elevation in meters to format
 * @param unit The distance unit to use for formatting the elevation
 * @returns The formatted elevation text to display
 */
function formatElevationText(
  elevationInMeters: number | undefined,
  unit: DistanceUnitTypes,
): string {
  if (elevationInMeters === undefined) return "";
  return Number(formatDistanceDecimal(elevationInMeters, unit, false)).toFixed(
    2,
  );
}

/**
 * Parse user-entered text into a numeric value, or undefined when the text is
 * empty or not a valid number.
 *
 * @param text The user-entered text to parse
 * @returns The parsed numeric value, or undefined if the text is empty or invalid
 */
function parseElevationText(text: string): number | undefined {
  if (text.trim() === "") return undefined;
  const value = Number(text);
  return Number.isNaN(value) ? undefined : value;
}

type SheetsElevationDialogContentProps = {
  /** The name of the area for which the elevation is being set, to be shown in the dialog content */
  areaName: string;
  /** The default elevation value, in meters, to be shown in the dialog content */
  defaultElevation?: number;
  /** The user's preferred distance unit. */
  distanceUnit: DistanceUnitTypes;
  /** Callback executed whenever the validation error changes. */
  onErrorChange?(error: string | undefined): void;
  /**
   * Callback executed whenever the entered value changes.
   * Receives the elevation in meters, or undefined when the input is not a valid number.
   */
  onValueChange?(elevationInMeters?: number): void;
};

function SheetsElevationDialogContent({
  areaName,
  defaultElevation,
  distanceUnit,
  onErrorChange,
  onValueChange,
}: SheetsElevationDialogContentProps): JSX.Element {
  // The elevation is stored in meters, but displayed and edited in the user's
  // preferred distance unit, formatted with two decimal digits.
  const defaultElevationText = useMemo(
    () => formatElevationText(defaultElevation, distanceUnit),
    [defaultElevation, distanceUnit],
  );

  const [text, setText] = useState(defaultElevationText);
  const [error, setError] = useState<string>();

  const onTextChanged = useCallback(
    (newText: string) => {
      // Reject input with more than two digits after the decimal separator.
      if (NUMBER_PRECISION_REGEX.test(newText)) return;
      setText(newText);

      // Convert the typed value (in the displayed unit) back to meters
      const value = parseElevationText(newText);
      const meters =
        value === undefined
          ? undefined
          : convertDistanceToMeter(value, distanceUnit);

      const newError =
        value === undefined ? "Please enter a valid number" : undefined;
      setError(newError);
      onErrorChange?.(newError);

      onValueChange?.(meters);
    },
    [distanceUnit, onErrorChange, onValueChange],
  );

  // Restore the original elevation.
  const onReset = useCallback(() => {
    setText(defaultElevationText);
    setError(undefined);
    onErrorChange?.(undefined);
    onValueChange?.(defaultElevation);
  }, [defaultElevation, defaultElevationText, onErrorChange, onValueChange]);

  const canReset = defaultElevationText !== "" && text !== defaultElevationText;

  return (
    <Stack sx={{ gap: 2, padding: 2 }}>
      <FaroText variant="bodyM">
        Set new elevation valid for all sheets of the selected area{" "}
        <TranslateVar name="areaName">{areaName}</TranslateVar>
      </FaroText>

      <Stack>
        <FaroText variant="heading14">Elevation </FaroText>
        <TextField
          type="number"
          text={text}
          error={error}
          fullWidth
          onTextChanged={onTextChanged}
          onFocus={(ev) => {
            ev.target.select();
          }}
          endAdornment={
            <InputAdornment position="end">
              <Stack direction="row" sx={{ alignItems: "center", gap: 0.5 }}>
                {getDistanceUnitSymbolText(distanceUnit)}
                {canReset && (
                  <FaroTooltip title="Reset to default">
                    <FaroIconButton
                      aria-label="reset-value"
                      size="s"
                      margin={0.5}
                      onClick={onReset}
                    >
                      <HistoryUndoIcon />
                    </FaroIconButton>
                  </FaroTooltip>
                )}
              </Stack>
            </InputAdornment>
          }
        />
      </Stack>
    </Stack>
  );
}
